/**
 * Formats a date string into a more readable format
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-'; // or '' if you prefer empty string, or 'N/A'

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Checks if a date string is valid
 * @param dateString - Date string to validate
 * @returns boolean indicating if the date is valid
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Formats a date to ISO string format (YYYY-MM-DD)
 * @param date - Date object or date string
 * @returns ISO formatted date string
 */
export const toISODateString = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
};

/*
 * Ported from front-porch/src/utils/dateUtils.ts, the reference
 * implementation. Kept identical so the two apps' comment threads read
 * the same; diff them rather than letting them drift.
 */

/**
 * The full timestamp, for a `title` attribute beside a relative one.
 *
 * A violation thread is a record that gets read back in a dispute, so the
 * exact time is never more than a hover away from the friendly one.
 */
export const formatTimestamp = (
  value: string | null | undefined
): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

/**
 * "3 hours ago", for a comment header.
 *
 * `formatDate` drops the time entirely, which turns a conversation into a
 * pile of identical dates. Pair this with {@link formatTimestamp} in a
 * `title`, never alone.
 *
 * Hand-rolled rather than `Intl.RelativeTimeFormat`: that varies its
 * wording with locale and ICU version, which makes it awkward to assert,
 * and it has no "just now" or "yesterday" — the two buckets a thread
 * needs most.
 *
 * Anything older than a week falls through to the absolute timestamp.
 * "47 days ago" is arithmetic the reader has to undo; a date is not.
 */
export const formatRelativeTime = (
  value: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // A clock skew between the browser and the server can date a comment
  // slightly in the future. "in 4 seconds" reads as a bug; "just now" is
  // both truthful enough and what the reader expects.
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;

  return formatTimestamp(value);
};
